import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface InfoTooltipProps {
  title: string;
  description: string;
  usage?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export function InfoTooltip({ title, description, usage, position = 'top' }: InfoTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipStyles, setTooltipStyles] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (!isVisible || !triggerRef.current || !tooltipRef.current) return;

    const updatePosition = () => {
      const trigger = triggerRef.current?.getBoundingClientRect();
      const tooltip = tooltipRef.current?.getBoundingClientRect();
      
      if (!trigger || !tooltip) return;

      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      let top = 0;
      let left = 0;

      switch (position) {
        case 'top':
          top = trigger.top + scrollY - tooltip.height - 8;
          left = trigger.left + scrollX + (trigger.width / 2) - (tooltip.width / 2);
          // Flip to bottom if not enough space
          if (trigger.top - tooltip.height < 0) {
            top = trigger.bottom + scrollY + 8;
          }
          break;
        case 'bottom':
          top = trigger.bottom + scrollY + 8;
          left = trigger.left + scrollX + (trigger.width / 2) - (tooltip.width / 2);
          // Flip to top if not enough space
          if (trigger.bottom + tooltip.height > window.innerHeight) {
            top = trigger.top + scrollY - tooltip.height - 8;
          }
          break;
        case 'left':
          top = trigger.top + scrollY + (trigger.height / 2) - (tooltip.height / 2);
          left = trigger.left + scrollX - tooltip.width - 8;
          // Flip to right if not enough space
          if (trigger.left - tooltip.width < 0) {
            left = trigger.right + scrollX + 8;
          }
          break;
        case 'right':
          top = trigger.top + scrollY + (trigger.height / 2) - (tooltip.height / 2);
          left = trigger.right + scrollX + 8;
          // Flip to left if not enough space
          if (trigger.right + tooltip.width > window.innerWidth) {
            left = trigger.left + scrollX - tooltip.width - 8;
          }
          break;
      }

      // Keep tooltip within viewport bounds
      left = Math.max(8, Math.min(left, window.innerWidth - tooltip.width - 8));
      top = Math.max(8, Math.min(top, window.innerHeight + scrollY - tooltip.height - 8));

      setTooltipStyles({
        position: 'absolute',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 99999
      });
    };

    // Initial position
    updatePosition();

    // Update position on scroll and resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isVisible, position]);

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => setIsVisible(!isVisible)}
        className="p-1.5 text-gray-400 hover:text-blue-400 transition-colors rounded-full 
                   hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        aria-label={`Info about ${title}`}
      >
        <Info className="w-4 h-4" />
      </button>

      {isVisible && createPortal(
        <AnimatePresence>
          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.1 }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={tooltipStyles}
            className="min-w-[240px] max-w-[320px] glass-panel p-4 shadow-xl 
                     border border-blue-500/20 backdrop-blur-sm text-left"
          >
            <div className="space-y-2">
              <h4 className="font-medium text-blue-300">{title}</h4>
              <p className="text-sm text-gray-300 leading-relaxed">{description}</p>
              {usage && (
                <div className="mt-3 pt-3 border-t border-gray-700/50">
                  <p className="text-sm text-gray-400">
                    <span className="text-blue-400 font-medium">Usage: </span>
                    {usage}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}